import { FormControlLabel, Radio, RadioGroup, Stack, Typography } from "@mui/material";
import ImageUploaderWithPreview from "@entrilia/shared/components/imageUploader/ImageUploaderWithPreview";
import type { SignOnSettings } from "../../../../api/types/investorPortalTypes";
import EditColorSection from "./EditColorSection";
import type { PortalStyleSettings } from "./investorPortalStyleState";
import SectionTitle from "./SectionTitle";

interface Props {
  settings: PortalStyleSettings;
  signOnSettings: SignOnSettings;
  disabled: boolean;
  isSaving: boolean;
  onChange: (update: Partial<PortalStyleSettings>) => void;
}

const imageTypes = ["image/png", "image/jpeg"];

const SignInPageSettings = ({ settings, signOnSettings, disabled, isSaving, onChange }: Props) => (
  <Stack spacing={4}>
    <Typography color="textSecondary">
      Choose how the sign in page looks. Set either color or image as the background.
    </Typography>

    <Stack spacing={2}>
      <SectionTitle text="Logotype on Sign in page" description="PNG or JPG. Recommended size: 280x56 pixels." />
      <ImageUploaderWithPreview
        disabled={disabled || isSaving}
        width="100%"
        height={56}
        allowedImageTypes={imageTypes}
        originalImageSrc={signOnSettings.clientLogoUrl}
        imageSrc={settings.clientLogoUrl}
        onImageSelected={(clientLogoFile, clientLogoUrl) => onChange({ clientLogoFile, clientLogoUrl })}
        uploadButtonName="Upload"
        maxFileSizeBytes={1_000_000}
        border="dashed"
        buttonsPosition="bottom"
        Placeholder={
          <Stack sx={{ p: 2 }}>
            <Typography variant="h5" color="textDisabled">
              Logotype
            </Typography>
          </Stack>
        }
      />
    </Stack>

    <EditColorSection
      title="Theme color"
      subtitle="The color of buttons and links on login pages."
      color={settings.primaryThemeColor}
      onChange={(primaryThemeColor) => onChange({ primaryThemeColor })}
      disabled={disabled}
    />

    <Stack spacing={2}>
      <SectionTitle text="Background" />
      <RadioGroup
        value={settings.useBackgroundImage}
        sx={{ rowGap: 3 }}
        onChange={(e) => onChange({ useBackgroundImage: e.target.value === "true" })}
      >
        <FormControlLabel
          value="false"
          disabled={disabled}
          control={<Radio />}
          slotProps={{ typography: { sx: { width: "100%" } } }}
          sx={{ mr: 0 }}
          label={
            <EditColorSection
              title="Background color"
              subtitle="Background color of the sign in page."
              color={settings.backgroundColor}
              onChange={(backgroundColor) => onChange({ backgroundColor })}
              disabled={disabled || settings.useBackgroundImage}
            />
          }
        />
        <FormControlLabel
          value="true"
          disabled={disabled}
          control={<Radio />}
          slotProps={{ typography: { sx: { width: "100%" } } }}
          sx={{ mr: 0, alignItems: "flex-start" }}
          label={
            <SectionTitle
              text="Background image"
              description="Set an image as a background for the sign in page. PNG or JPG. Recommended size: 2560x1600 pixels."
            />
          }
        />
      </RadioGroup>
      <ImageUploaderWithPreview
        disabled={disabled || isSaving || !settings.useBackgroundImage}
        width={128}
        height={80}
        allowedImageTypes={imageTypes}
        originalImageSrc={signOnSettings.backgroundImageUrl}
        imageSrc={settings.backgroundImageUrl}
        onImageSelected={(backgroundImageFile, backgroundImageUrl) =>
          onChange({ backgroundImageFile, backgroundImageUrl })
        }
        uploadButtonName="Upload"
        backgroundSize="cover"
        maxFileSizeBytes={2_000_000}
        buttonsPosition="right"
      />
    </Stack>
  </Stack>
);

export default SignInPageSettings;
